import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to fetch data based on dynamic parameters.
 * @param {function} apiFunction - The API request function.
 * @param {object|null|undefined} params - Optional parameters for the API request.
 * @returns {object} { data, isLoading, error, refetch }
 */
const useFetchData = (apiFunction, params) => {
	const [data, setData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	const fetchData = useCallback(
		async () => {
			setIsLoading(true);
			setError(null);

			try {
				const response = params
					? await apiFunction(params) // Pass params if provided
					: await apiFunction(); // Call without params if none are provided

				setData(response);
			} catch (err) {
				setError(err?.message || 'Error fetching data');
			} finally {
				setIsLoading(false);
			}
		},
		[apiFunction, params]
	);

	useEffect(
		() => {
			fetchData();
		},
		[fetchData, params]
	);

	return { data, isLoading, error, refetch: fetchData };
};

export default useFetchData;
